import {
  type EventCardProps,
  type FireflyKebabMenuItem,
  type GroupEventCardProps,
  type HostInfo,
  ChakraBox,
  DateFormat,
  EventCard,
  FAIconName,
  formatProviderPronounsForDisplay,
  GroupEventCard,
  useToastMessage,
} from '@equip.health/ui';
import { useMemo, useState } from 'react';

import ConfirmModal from '~/components/common/ConfirmModal';
import AddHostsModal from '~/components/maud/calendar/group-class/modals/AddHostsModal';
import GroupClassDetailsModal from '~/components/maud/calendar/group-class/modals/GroupClassDetailsModal';
import { capitalizeFirstLetter } from '~/lib/common';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import {
  CANCEL_EVENT_MODAL,
  ParticipantTypes,
} from '~/lib/constants/schedule.constants';
import { GroupClassEventOption } from '~/lib/constants/scheduling.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  formatDateWithTimezone,
  getAttendeePronouns,
  Recurrence,
  shouldShowPatientProfileLink,
} from '~/lib/util/calendar.util';
import { generateHostInitials } from '~/lib/util/groups.utils';
import {
  isPastDateTime,
  isUserAttendingEvent,
} from '~/lib/util/classes/groupClasses.util';
import { getAgeFromDOB } from '~/lib/util/patient.util';
import {
  getGroupClassEventOptions,
  getRecurrenceDisplay,
  isAddNoteAllowed,
  ScheduleType,
} from '~/lib/util/schedule/schedule.util';
import { Path } from '~/routes/constants';
import {
  useCancelEventMutation,
  useDeclineEventMutation,
  useGetGroupClassEventDetailsQuery,
  useLazyGetGroupClassDetailQuery,
  useUpdateGroupSectionEventV2Mutation,
} from '~/store/services/groupClass';
import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { useGetAppointmentTypesQuery } from '~/store/services/schedule';
import {
  EditAppointmentEvents,
  EditAppointmentSource,
} from '~/analytics/patientEvents';
import { DateTime } from 'luxon';
import errorConstant from '~/lib/constants/error.constants';
import {
  getDisplayNameOrLegalNameForProvider,
  getProviderStateAtActionTime,
} from '~/lib/util/user.util';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useIsPatientPath } from '~/lib/hooks/useIsPatientPath';
import EditAppointmentModal from '../../EditAppointment';
import useAppointments from '~/lib/hooks/useAppointments';
import { NoteEventAttribute, NoteEvents } from '~/analytics/notesEvents';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { isEligibleToEditAppointment } from '../../EditAppointment/util';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
import { useFlags } from 'launchdarkly-react-client-sdk';
import GroupClassDetailsDrawer from '~/components/maud/calendar/groupsV2/GroupClassDetailsView';
import ViewRegistrantsModal from '../../Modals/ViewRegistrantsModal';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';

export interface EventDayCardProps {
  onCancelAppointment?: (externalId: string) => void;
  shouldCloseModal?: () => void;
  onNoteButtonClick?: (externalId: string) => void;
  onEventCardClick?: (externalId: string) => void;
  onZoomClick?: (externalId: string) => void;
  scheduleItem: ScheduleItemBase;
  onNextScheduleDateClicked?: (nextDate: string) => void;
}

export const getAttendeeName = (name: string) => {
  const nameParts = name?.split(' ');
  const index = nameParts?.findIndex((part) => part?.includes('"'));

  return index !== -1
    ? nameParts?.length > 0 &&
        `${nameParts[index]?.replaceAll('"', '')} ${nameParts?.slice(index + 1)?.join(' ')}`
    : name;
};

interface GetNoteButtonParams {
  scheduleItem: ScheduleItemBase;
  isNotAuthorOfApptNote: boolean;
  isAddNoteDisabled: boolean;
  isShowNoteButton: boolean;
  isNew?: boolean;
}

interface GetNoteButtonResult {
  icon: FAIconName;
  shouldDisableNoteButton: boolean;
  shouldShowNoteButton: boolean;
  title: string;
}

interface GetNoteButtonNewResult {
  icon: FAIconName;
  isDisabled: boolean;
  shouldDisableNoteButton: boolean;
  shouldShowNoteButton: boolean;
  title: string;
}

const getNoteButtonCommon = ({
  scheduleItem,
  isNotAuthorOfApptNote,
  isAddNoteDisabled,
}: Omit<GetNoteButtonParams, 'isShowNoteButton' | 'isNew'>) => {
  let note: string;
  let leftIcon: FAIconName;

  const isDisabled =
    isNotAuthorOfApptNote ||
    (isAddNoteDisabled && !scheduleItem.isNoteInSignedState);

  const isAddNote =
    !scheduleItem.isNoteInSignedState && !scheduleItem.isNoteInDraftState;

  if (isAddNote) {
    note = 'Add note';
    leftIcon = FAIconName.plusRegular;
  } else if (scheduleItem.isNoteInDraftState) {
    note = 'Edit draft';
    leftIcon = FAIconName.penRegular;
  } else {
    note = 'View note';
    leftIcon = FAIconName.fileRegular;
  }

  return { note, leftIcon, isDisabled };
};

export function getNoteButton(
  params: GetNoteButtonParams & { isNew?: false },
): GetNoteButtonResult;
export function getNoteButton(
  params: GetNoteButtonParams & { isNew: true },
): GetNoteButtonNewResult | undefined;

export function getNoteButton({
  scheduleItem,
  isNotAuthorOfApptNote,
  isAddNoteDisabled,
  isShowNoteButton,
  isNew,
}: GetNoteButtonParams):
  | GetNoteButtonResult
  | GetNoteButtonNewResult
  | undefined {
  const { note, leftIcon, isDisabled } = getNoteButtonCommon({
    scheduleItem,
    isNotAuthorOfApptNote,
    isAddNoteDisabled,
  });

  if (isNew) {
    return isShowNoteButton
      ? {
          icon: leftIcon,
          isDisabled: isDisabled,
          shouldDisableNoteButton: isDisabled,
          shouldShowNoteButton: isShowNoteButton,
          title: note,
        }
      : undefined;
  }

  return {
    icon: leftIcon,
    shouldDisableNoteButton: isDisabled,
    shouldShowNoteButton: isShowNoteButton,
    title: note,
  };
}

const TOP_LEVEL_ID = 'event-day-card';

const EventDayCard = (eventCardProps: EventDayCardProps) => {
  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();
  const {
    scheduleItem,
    shouldCloseModal,
    onCancelAppointment,
    onNoteButtonClick,
    onEventCardClick,
    onZoomClick,
    onNextScheduleDateClicked,
  } = eventCardProps;
  const { trackEvent } = useAnalytics();
  const isPatientPath = useIsPatientPath();
  const { data: groupClassEvents } = useGetGroupClassEventDetailsQuery(
    scheduleItem.externalId,
    {
      skip:
        scheduleItem.typeOf === ScheduleType.APPOINTMENT ||
        isGroupsRestructuringEnabled,
    },
  );

  const [cancelEvent, { isLoading: isCancelEventLoading }] =
    useCancelEventMutation();

  const [
    updateGroupSectionEventV2,
    { isLoading: isUpdatingGroupSectionEvent },
  ] = useUpdateGroupSectionEventV2Mutation();

  const [declineEvent] = useDeclineEventMutation();

  const [isCancelEventWarningModalOpen, setIsCancelEventWarningModalOpen] =
    useState<boolean>(false);

  const [selectedGroupEventId, setSelectedGroupEventId] = useState<
    string | undefined
  >(undefined);

  const [
    isConfirmScheduleAppointmentModalOpen,
    setIsConfirmScheduleAppointmentModalOpen,
  ] = useState<boolean>(false);

  const [isHostsModalOpen, setIsHostsModalOpen] = useState<boolean>(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [isViewRegistrantsModalOpen, setViewRegistrantsModalOpen] =
    useState<boolean>(false);

  const handleDiscardCancelEvent = (): void => {
    setIsCancelEventWarningModalOpen(false);
  };

  const [getGroupClass, { data: groupClass }] =
    useLazyGetGroupClassDetailQuery();

  const handleConfirmCancelEvent = (): void => {
    const request = shouldUseGroupEventCard
      ? updateGroupSectionEventV2({
          groupEventExternalId: scheduleItem.externalId,
          hosts: [],
          isCancel: true,
          isDecline: false,
        })
      : cancelEvent(scheduleItem.externalId);

    request
      .unwrap()
      .then(() => {
        setIsCancelEventWarningModalOpen(false);
        shouldCloseModal?.();
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: `${TOP_LEVEL_ID}__cancel-event-error`,
          text: 'Unable to cancel this event. Please try again.',
          variant: 'critical',
        });
      });
  };

  const {
    currentUser: { userId: userExternalId },
    permissions,
  } = useGlobalContext();

  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];
  const hostIds =
    (scheduleItem.hosts ?? []).map(
      (host: ScheduleUserBase) => host.externalId,
    ) ?? [];
  const isHost = hostIds.includes(userExternalId);
  const isAttendee = isUserAttendingEvent(
    scheduleItem?.attendees,
    userExternalId,
  );

  const { isDisabled: isAddNoteDisabled, isHidden: isAddNoteHidden } =
    isAddNoteAllowed(
      scheduleItem.typeOf,
      isHost,
      isAttendee,
      isSchedulingAdmin,
    );

  const { data: appointmentTypes } = useGetAppointmentTypesQuery();

  const isShowNoteButton =
    (scheduleItem.isNoteInSignedState || !isAddNoteHidden) &&
    !(isPatientPath && scheduleItem?.groupClassExternalId);

  const isNoteAuthor = scheduleItem.noteAuthoredByProviderId === userExternalId;

  const isNotAuthorOfApptNote =
    scheduleItem.typeOf === ScheduleType.APPOINTMENT &&
    scheduleItem.isNoteInDraftState &&
    !isNoteAuthor;

  const getTime = (date: string): string =>
    formatDateWithTimezone(date, DateFormat.timeWithoutSpace, undefined, true);

  const eventType =
    scheduleItem.typeOf == 'Appointment' ? 'appointment' : 'group-class';

  const isGroupClassEvent = scheduleItem.typeOf === 'Group Class Event';

  const shouldUseGroupEventCard =
    isGroupsRestructuringEnabled && isGroupClassEvent;

  const eventDate = formatDateWithTimezone(
    scheduleItem.startDateTime,
    `${DateFormat.weekdayShortMonthDateNoSpecialChar}`,
    undefined,
  );

  const onViewClassDetails = (): void => {
    if (shouldUseGroupEventCard) {
      setSelectedGroupEventId(scheduleItem?.groupClassExternalId);
      trackEvent(GroupSectionEvents.groupClassDetailsDrawerViewed, {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
      });
    } else {
      getGroupClass({
        groupClassExternalId: scheduleItem?.groupClassExternalId,
      });
      setIsDetailsModalOpen(true);
    }
  };

  const { createToast } = useToastMessage();

  const onDeclineEvent = (): void => {
    const request = shouldUseGroupEventCard
      ? updateGroupSectionEventV2({
          groupEventExternalId: scheduleItem.externalId,
          hosts: [],
          isCancel: false,
          isDecline: true,
        })
      : declineEvent(scheduleItem.externalId);

    request
      .unwrap()
      .then(() => {
        shouldCloseModal();
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: `${TOP_LEVEL_ID}`,
          text: errorConstant.DeclineEventError,
          variant: 'critical',
        });
      });
  };

  const onCancelEvent = (): void => {
    setIsCancelEventWarningModalOpen(true);
  };
  const recurringUntilDateTime: DateTime = DateTime.fromISO(
    scheduleItem.recurringUntilDateTime,
  );
  const diffInDays: number = Math.ceil(
    recurringUntilDateTime.diff(DateTime.now(), 'days').days,
  );

  const shouldShowEndDate: boolean = diffInDays <= 90;

  const frequencyText = getRecurrenceDisplay(
    scheduleItem.startDateTime,
    scheduleItem?.frequency,
    scheduleItem.recurringUntilDateTime,
    true,
    shouldShowEndDate,
  );

  const attendees = scheduleItem?.attendees?.map((attendee) => {
    let displayName = '';
    if (attendee?.displayFirstName && attendee?.displayLastName) {
      displayName = `${attendee.displayFirstName} ${attendee.displayLastName}`;
    }
    let providerState: ProviderHistory;
    if (attendee?.providerType) {
      providerState = getProviderStateAtActionTime(
        attendee?.providerHistory,
        scheduleItem?.createdAt,
      );
    }

    const updatedAttendee = {
      externalId: attendee.externalId,
      name: displayName || getAttendeeName(attendee?.name),
      userType: providerState?.providerTypeDetails?.name || attendee?.type,
      pronouns: getAttendeePronouns([
        formatProviderPronounsForDisplay(
          attendee?.pronouns,
          attendee?.otherPronounText,
        ),
      ]),
    };
    if (attendee?.isHost)
      Object.assign(updatedAttendee, { isHost: attendee?.isHost });

    if (attendee.credentialsDetails) {
      const credentials = attendee.credentialsDetails?.map((c) => c.code);
      Object.assign(updatedAttendee, { credentials });
    }

    if (attendee?.avatar) {
      Object.assign(updatedAttendee, {
        avatarUrl: attendee?.avatar?.thumbnailUrl ?? attendee?.avatar?.url,
      });
    }
    const oooDate = getLatestOutOfOfficeEndDate(attendee?.outOfOffice);
    Object.assign(updatedAttendee, { oooDate });

    return updatedAttendee;
  });

  const patient = {
    age: getAgeFromDOB(
      scheduleItem?.patient?.dateOfBirth,
      DateFormat.shortYearMonthDay,
    ),
    hasGivenRecordingConsent: scheduleItem?.patient?.hasGivenRecordingConsent,
    name: `${capitalizeFirstLetter(scheduleItem?.patient?.chosenName || scheduleItem?.patient?.firstName)} ${capitalizeFirstLetter(scheduleItem?.patient?.lastName)}`,
  };

  const [editedAppointment, setEditedAppointment] =
    useState<ScheduleItemBase>(undefined);

  const { getProviderTeamCareTeam, patientCareTeams } = useAppointments();

  const source = isPatientPath
    ? EditAppointmentSource.patientCalendarTab
    : EditAppointmentSource.providerCalendarTab;

  const handleEditAppointmentClick = () => {
    trackEvent(EditAppointmentEvents.editAppointmentClicked, {
      appointmentId: scheduleItem.externalId,
      isRecurringEvent:
        !!scheduleItem?.frequency &&
        scheduleItem?.frequency !== Recurrence.DOES_NOT_REPEAT.toLowerCase(),
      source,
    });
    setEditedAppointment(scheduleItem);
    getProviderTeamCareTeam(scheduleItem.patient.userId);
  };

  const getAppointmentKebabMenu = () => {
    if (!(isHost || isSchedulingAdmin) || scheduleItem.isCancelled) return [];

    const kebabOptions = [];
    const canEdit = isEligibleToEditAppointment({
      appointmentTypes,
      scheduleItem,
    });

    if (
      canEdit &&
      !scheduleItem.isNoteInSignedState &&
      new Date(scheduleItem.startDateTime) >= new Date()
    ) {
      kebabOptions.push({
        label: 'Edit appointment',
        leftIcon: FAIconName.penRegular,
        leftIconColor: 'neutral.primary',
        onClick: handleEditAppointmentClick,
      });
    }

    kebabOptions.push({
      label: 'Cancel appointment',
      leftIcon: FAIconName.trashRegular,
      leftIconColor: 'neutral.primary',
      onClick: () => onCancelAppointment?.(scheduleItem.externalId),
    });

    return kebabOptions;
  };

  const kebabMenuOptionsList: Partial<
    Record<
      GroupClassEventOption,
      {
        label: string;
        leftIcon: FAIconName;
        leftIconColor: string;
        onClick: () => void;
      }
    >
  > = {
    [GroupClassEventOption.VIEW_CLASS_INFO]: {
      label: 'View group details',
      leftIcon: FAIconName.infoCircleRegular,
      leftIconColor: 'neutral.primary',
      onClick: onViewClassDetails,
    },
    [GroupClassEventOption.DECLINE]: {
      label: 'Decline this event',
      leftIcon: FAIconName.eyeSlashRegular,
      leftIconColor: 'neutral.primary',
      onClick: onDeclineEvent,
    },
    [GroupClassEventOption.ADD_HOST]: {
      label: 'Add host for this event',
      leftIcon: FAIconName.userPlusRegular,
      leftIconColor: 'neutral.primary',
      onClick: () => setIsHostsModalOpen(true),
    },
    [GroupClassEventOption.CANCEL]: {
      label: 'Cancel this event',
      leftIcon: FAIconName.XmarkRegular,
      leftIconColor: 'critical.base',
      onClick: onCancelEvent,
    },
  };

  const groupClassEventKebabMenuListV2: FireflyKebabMenuItem[] = useMemo(() => {
    const options = getGroupClassEventOptions(
      hostIds.includes(userExternalId),
      isAttendee,
      permissions[MaudPermissions.MAUD_SCHEDULE_PROXY],
      isPastDateTime(scheduleItem.endDateTime),
      isPatientPath,
      hostIds?.length === 1,
    );
    const shouldShowKebabOption = (option: {
      isDisabled: boolean;
      isHidden: boolean;
    }) => {
      return !option.isHidden && !option.isDisabled;
    };

    return Object.entries(kebabMenuOptionsList)
      .map(([option, value]) => shouldShowKebabOption(options[option]) && value)
      .filter((each) => each);
  }, [hostIds, isAttendee, permissions, userExternalId]);

  const getGroupClassAttendees = () => {
    if (!groupClassEvents) return [];

    const attendees = [];
    [
      ...(groupClassEvents.attendees || []),
      ...(groupClassEvents?.declinedUsers || []),
    ].forEach((attendee) => {
      const name =
        attendee?.type === 'provider'
          ? getDisplayNameOrLegalNameForProvider(
              attendee?.displayFirstName,
              attendee.firstName,
              attendee?.displayLastName,
              attendee.lastName,
            )
          : `${attendee?.chosenName || attendee.firstName} ${attendee.lastName}`;
      attendees.push({
        externalId: attendee.externalId,
        isAttended: !!groupClassEvents?.attendees?.includes(attendee),
        name,
      });
    });

    return attendees;
  };

  const getGroupClassHosts = () => {
    if (!groupClassEvents) return [];

    return groupClassEvents.hosts.map((host) => {
      const providerTypeAtClassTime = getProviderStateAtActionTime(
        host?.providerHistory,
        groupClassEvents?.startTime,
      );

      const providerType =
        providerTypeAtClassTime?.providerTypeDetails?.name ||
        host?.providerType;

      return {
        externalId: host.externalId,
        isHost: true,
        name: generateHostInitials(host),
        userType: providerType,
        pronouns: getAttendeePronouns([
          formatProviderPronounsForDisplay(
            host?.pronouns,
            host?.otherPronounText,
          ),
        ]),
        avatarUrl: host?.avatar?.thumbnailUrl ?? host?.avatar?.url,
        credentials: host?.credentialsDetails?.map((c) => c?.code),
        oooDate: getLatestOutOfOfficeEndDate(host?.outOfOffice),
      };
    });
  };

  const getGroupEventHosts = (): HostInfo[] => {
    if (!scheduleItem?.hosts?.length) return [];

    return scheduleItem.hosts.map((host) => ({
      id: host.externalId,
      name: generateHostInitials(host),
      src: host?.avatar?.thumbnailUrl ?? host?.avatar?.url,
      userType:
        host?.type === ParticipantTypes.PROVIDER ||
        host?.type === ParticipantTypes.PATIENT ||
        host?.type === ParticipantTypes.SUPPORT
          ? host.type
          : ParticipantTypes.PROVIDER,
    }));
  };

  const eventName = `${scheduleItem.title}${eventType === 'appointment' ? `, ${scheduleItem.duration} minutes` : ''}`;

  const handleNoteButtonClick = (externalId: string) => {
    const isAddNote =
      !scheduleItem.isNoteInSignedState && !scheduleItem.isNoteInDraftState;
    const isEditDraft = scheduleItem.isNoteInDraftState;

    if (!isGroupsRestructuringEnabled) {
      const noteEventParams = {
        [NoteEventAttribute.AppointmentId]: externalId,
        [NoteEventAttribute.EventType]: eventType,
        [NoteEventAttribute.Source]: isPatientPath
          ? 'Patient Calendar'
          : 'Provider Calendar',
      };

      if (isAddNote) {
        trackEvent(
          NoteEvents.addNoteAppointmentsTabButtonClicked,
          noteEventParams,
        );
      } else if (isEditDraft) {
        trackEvent(
          NoteEvents.editNoteAppointmentsTabButtonClicked,
          noteEventParams,
        );
      } else {
        trackEvent(
          NoteEvents.viewNoteAppointmentsTabButtonClicked,
          noteEventParams,
        );
      }
    }

    onNoteButtonClick?.(externalId);
  };

  const handleViewAllButtonClick = () => {
    setViewRegistrantsModalOpen(true);
  };

  const patientUserId = scheduleItem?.patient?.userId;
  const patientProfileLink = patientUserId
    ? `${globalThis?.location?.origin}${Path.MAUD_PATIENTS}/${patientUserId}`
    : undefined;

  const embellishedEventCardProps: EventCardProps = {
    eventType,
    kebabItems:
      eventType === 'appointment'
        ? getAppointmentKebabMenu()
        : groupClassEventKebabMenuListV2,
    onEventCardClick,
    onNoteButtonClick: handleNoteButtonClick,
    patientProfileLink,
    onZoomClick,
    scheduleItem: {
      attendees:
        eventType === 'appointment' ? attendees : getGroupClassAttendees(),
      eventDate,
      eventExternalId: scheduleItem.externalId,
      ...(scheduleItem?.attendees?.length > 0 && {
        attendeesCount: scheduleItem?.attendees?.length,
        eventHosts: getGroupClassHosts(),
      }),
      eventName,
      eventTime: `${getTime(scheduleItem.startDateTime)} - ${getTime(scheduleItem.endDateTime)}`,
      frequencyText,
      noteButton: getNoteButton({
        scheduleItem,
        isNotAuthorOfApptNote,
        isAddNoteDisabled,
        isShowNoteButton,
      }),
      ...(eventType === 'appointment' && { patient }),

      shouldShowViewPatientProfileButton: shouldShowPatientProfileLink(
        scheduleItem?.patient?.userId,
      ),
      shouldShowZoomButton: scheduleItem.isShowZoomButton,
      isCancelled: scheduleItem.isCancelled,
      cancelledReason: scheduleItem.cancelledReason,
    },
  };

  const totalSessions = scheduleItem?.length?.split('_').pop();
  const weeksRemaining =
    totalSessions && scheduleItem.currentSession != null
      ? `Session ${scheduleItem.currentSession} of ${totalSessions}`
      : '';

  const noteButtonProps = useMemo(() => {
    const buttonResult = getNoteButton({
      scheduleItem,
      isNotAuthorOfApptNote,
      isAddNoteDisabled,
      isShowNoteButton,
      isNew: true,
    });

    return buttonResult
      ? {
          ...buttonResult,
          onNoteClick: () => handleNoteButtonClick(scheduleItem.externalId),
        }
      : undefined;
  }, [
    scheduleItem,
    isNotAuthorOfApptNote,
    isAddNoteDisabled,
    isShowNoteButton,
    scheduleItem.externalId,
  ]);

  const groupEventCardProps: GroupEventCardProps = {
    id: scheduleItem.externalId,
    eventDate,
    eventTime: `${getTime(scheduleItem.startDateTime)} - ${getTime(
      scheduleItem.endDateTime,
    )}`,
    eventName: scheduleItem.title,
    recurrence: frequencyText,
    weeksRemaining,
    eventHosts: getGroupEventHosts(),
    registeredUsersCount: scheduleItem.registrantsCount ?? 0,
    kebabItems: groupClassEventKebabMenuListV2,
    shouldShowZoomButton: scheduleItem.isShowZoomButton,
    onZoomClick: () => onZoomClick?.(scheduleItem.externalId),
    onEventCardClick: () => onEventCardClick?.(scheduleItem.externalId),
    noteButton: noteButtonProps,
    onViewAllClick: () => handleViewAllButtonClick(),
    tooltipWrapper: ({ children, providerExternalId }) => (
      <ProviderAvatarTooltipWrapper
        actionTime={scheduleItem.startDateTime}
        isRoleAtAction
        placement="left"
        providerExternalId={providerExternalId}
      >
        {children}
      </ProviderAvatarTooltipWrapper>
    ),
  };

  const handleEditModalClose = (date?: string) => {
    if (date) onNextScheduleDateClicked?.(date);
    setEditedAppointment(undefined);
    shouldCloseModal();
  };

  const card = shouldUseGroupEventCard ? (
    <GroupEventCard {...groupEventCardProps} />
  ) : (
    <EventCard {...embellishedEventCardProps} />
  );

  return (
    <>
      <ChakraBox padding="6px">{card}</ChakraBox>
      {shouldUseGroupEventCard && (
        <ViewRegistrantsModal
          id={scheduleItem.externalId}
          isOpen={isViewRegistrantsModalOpen}
          onViewRegistrantsListClose={() => setViewRegistrantsModalOpen(false)}
        />
      )}
      <ConfirmModal
        body={CANCEL_EVENT_MODAL.message}
        cancelLabel={CANCEL_EVENT_MODAL.cancelButtonLabel}
        confirmLabel={CANCEL_EVENT_MODAL.confirmButtonLabel}
        confirmVariant="accent.critical"
        id={`${TOP_LEVEL_ID}__cancel-event-warning`}
        isLoading={isCancelEventLoading || isUpdatingGroupSectionEvent}
        isOpen={isCancelEventWarningModalOpen}
        onClose={handleDiscardCancelEvent}
        onConfirm={handleConfirmCancelEvent}
        title={CANCEL_EVENT_MODAL.title}
      />
      <AddHostsModal
        groupClassEventExternalId={scheduleItem.externalId}
        hostIds={hostIds}
        isOpen={isHostsModalOpen}
        onClose={() => setIsHostsModalOpen(false)}
        useGroupSectionEventV2={shouldUseGroupEventCard}
      />
      <GroupClassDetailsModal
        groupClass={groupClass?.data}
        isOpen={isDetailsModalOpen}
        isRegisterEnabled={false}
        onClose={() => {
          setIsDetailsModalOpen(false);
        }}
        onRegistration={() => {
          getGroupClass({
            groupClassExternalId: scheduleItem?.groupClassExternalId,
          });
        }}
      />

      {selectedGroupEventId && (
        <GroupClassDetailsDrawer
          groupClassId={selectedGroupEventId}
          isOpen
          onClose={() => setSelectedGroupEventId(undefined)}
        />
      )}

      {editedAppointment && (
        <EditAppointmentModal
          editAppointment={editedAppointment}
          onClose={handleEditModalClose}
          patientCareTeams={patientCareTeams}
        />
      )}
      <DiscardChangesModalV2
        isOpen={isConfirmScheduleAppointmentModalOpen}
        onClose={() => setIsConfirmScheduleAppointmentModalOpen(false)}
        onConfirm={() => {
          setIsConfirmScheduleAppointmentModalOpen(false);
        }}
      />
    </>
  );
};
export default EventDayCard;
